import { useEffect } from 'react'
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  TimePicker,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { NameI18nFields } from './NameI18nFields'
import { policyOptionLabel } from '@/shared/lib/planName'
import {
  POLICY_STATES,
  createPolicy,
  fetchPolicies,
  policyDraftBody,
  updatePolicy,
  type Policy,
  type PolicyFormValues,
} from './catalogApi'
import {
  CLOCK_FORMAT,
  MAX_TIME_WINDOWS,
  TIME_WINDOW_KINDS,
  WEEK_DAYS,
  type TimeWindowFormRow,
} from './timeWindows'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface PolicyFormModalProps {
  open: boolean
  /** `null` = yangi siyosat */
  policy: Policy | null
  onClose: () => void
}

type FormValues = PolicyFormValues

const BYTES_IN_GB = 1024 ** 3

/**
 * Shubhali qisqa muddat chegarasi (soniya).
 *
 * <h2>⚠️ Bu chegara AMALDA SODIR BO'LGAN NOSOZLIKDAN keyin qo'shildi</h2>
 * `tarif-7m` siyosatiga `sessionTimeout = 2` yozilgan edi — operator
 * ehtimol "2 daqiqa" yoki "2 soat" ni nazarda tutgan. RADIUS esa qiymatni
 * SO'ZMA-SO'Z bajaradi: abonent ulanadi, ikki soniyadan keyin
 * `Session-Timeout` sababi bilan uziladi. `radpostauth` da esa
 * `Access-Accept` turadi, ya'ni "parol to'g'ri, lekin internet yo'q"
 * degan holat — sabab qidirish uchun eng yomon shakl.
 *
 * Maydonning o'zi bloklanmaydi: qisqa sessiya ATAYLAB kerak bo'lishi
 * mumkin (sinov, mehmon uchun demo). Lekin u endi jimgina o'tmaydi.
 */
const SUSPICIOUS_TIMEOUT_SECONDS = 300

/** Backenddagi `PolicyDraft` naqshining aynan o'zi: "50M/50M" */
const RATE_PATTERN = /^[0-9]+[kKmMgG]?\/[0-9]+[kKmMgG]?$/

/**
 * Burst naqshi — backenddagi `PolicyDraft.burst` bilan AYNAN bir xil.
 *
 * <h2>⚠️ X-SIB-003 — maydon butunlay tekshirilmasdi</h2>
 * `burst` `rate_limit` bilan BITTA `Mikrotik-Rate-Limit` atributiga
 * qo'shilib ketadi (`Policy.rateLimitWithBurst`). Ya'ni shakli buzuq
 * burst BUTUN atributni yaroqsiz qiladi va MikroTik tezlikni umuman
 * qo'llamaydi — bizning tomonda esa hammasi muvaffaqiyatli ko'rinadi
 * (`Access-Accept`, xato jurnalida hech narsa).
 *
 * Bo'laklar: `<son>/<son>` juftliklari va ixtiyoriy yakka son
 * (prioritet), bo'shliq bilan ajratilgan.
 */
const BURST_PATTERN = /^[0-9]+[kKmMgG]?\/[0-9]+[kKmMgG]?( +([0-9]+[kKmMgG]?\/[0-9]+[kKmMgG]?|[0-9]+))*$/

/**
 * Tayyor tezlik variantlari — `AutoComplete` uchun.
 *
 * ⚠️ Erkin kiritish SAQLANADI: tarif rejalari xilma-xil va sof `Select`
 * kam uchraydigan tezlikni umuman qo'yib bo'lmaydigan qilardi. Ro'yxat
 * — qulaylik; kafolatni naqsh beradi.
 */
const RATE_PRESETS = ['128k/128k', '1M/1M', '2M/2M', '5M/5M', '10M/10M', '20M/20M', '50M/50M', '100M/100M']

const WINDOW_ITEM_STYLE = { marginBottom: 8 }

function TimeWindowFields() {
  const { t } = useTranslation()
  const form = Form.useFormInstance<FormValues>()
  const rows: TimeWindowFormRow[] = Form.useWatch('timeWindows', form) ?? []

  const clock = (row: number, edge: 'from' | 'to') => (
    <Form.Item
      name={[row, edge]}
      label={t(`catalog.policy.window.${edge}`)}
      rules={[{ required: true, message: t('catalog.policy.window.timeRequired') }]}
      getValueProps={(value?: string | null) => ({
        value: value ? dayjs(value, CLOCK_FORMAT) : null,
      })}
      getValueFromEvent={(_time: unknown, text: string) => text || null}
      style={WINDOW_ITEM_STYLE}
    >
      <TimePicker
        format={CLOCK_FORMAT}
        showNow={false}
        needConfirm={false}
        style={{ width: '100%' }}
      />
    </Form.Item>
  )

  return (
    <Form.Item
      label={t('catalog.policy.window.title')}
      extra={<ExpandableHint text={t('catalog.policy.window.hint')} fontSize={14} />}
    >
      <Form.List name="timeWindows">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => {
              const kind = rows[field.name]?.kind
              return (
                <Card key={field.key} size="small" style={{ marginBottom: 8 }}>
                  <Row gutter={8} wrap={false} align="top">
                    <Col flex="auto">
                      <Form.Item
                        name={[field.name, 'kind']}
                        label={t('catalog.policy.window.kind')}
                        extra={kind ? t(`catalog.policy.window.kindHint.${kind}`) : undefined}
                        rules={[
                          { required: true, message: t('catalog.policy.window.kindRequired') },
                        ]}
                        style={WINDOW_ITEM_STYLE}
                      >
                        <Select
                          options={TIME_WINDOW_KINDS.map((option) => ({
                            value: option,
                            label: t(`catalog.policy.window.kindName.${option}`),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="none">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t('catalog.policy.window.remove')}
                        onClick={() => remove(field.name)}
                        style={{ marginTop: 30 }}
                      />
                    </Col>
                  </Row>
                  <Row gutter={8} align="top">
                    <Col flex="0 0 112px">{clock(field.name, 'from')}</Col>
                    <Col flex="0 0 112px">{clock(field.name, 'to')}</Col>
                    <Col flex="1 1 200px">
                      <Form.Item
                        name={[field.name, 'days']}
                        label={t('catalog.policy.window.days')}
                        style={WINDOW_ITEM_STYLE}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch={false}
                          maxTagCount="responsive"
                          placeholder={t('catalog.policy.window.everyDay')}
                          options={WEEK_DAYS.map((day) => ({
                            value: day,
                            label: t(`catalog.policy.window.day.${day}`),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {kind === 'SPEED' ? (
                    <Row gutter={8} align="top">
                      <Col flex="1 1 150px">
                        <Form.Item
                          name={[field.name, 'rateLimit']}
                          label={t('tariffs.rateLimit')}
                          rules={[
                            { required: true, message: t('catalog.policy.window.rateRequired') },
                            {
                              pattern: RATE_PATTERN,
                              message: t('tariffs.form.validation.rateFormat'),
                            },
                          ]}
                          style={WINDOW_ITEM_STYLE}
                        >
                          <AutoComplete
                            options={RATE_PRESETS.map((preset) => ({ value: preset }))}
                            placeholder="5M/5M"
                          />
                        </Form.Item>
                      </Col>
                      <Col flex="1 1 230px">
                        <Form.Item
                          name={[field.name, 'burst']}
                          label={t('catalog.policy.burst')}
                          rules={[
                            { pattern: BURST_PATTERN, message: t('catalog.policy.burstFormat') },
                          ]}
                          style={WINDOW_ITEM_STYLE}
                        >
                          <Input placeholder="10M/10M 8M/8M 10/10" />
                        </Form.Item>
                      </Col>
                    </Row>
                  ) : null}
                </Card>
              )
            })}
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              disabled={fields.length >= MAX_TIME_WINDOWS}
              onClick={() => add({ days: [...WEEK_DAYS] })}
            >
              {t('catalog.policy.window.add')}
            </Button>
          </>
        )}
      </Form.List>
    </Form.Item>
  )
}

export function PolicyFormModal({ open, policy, onClose }: PolicyFormModalProps) {
  const { t, i18n } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const policies = useQuery({
    queryKey: queryKeys.catalog.policies,
    queryFn: fetchPolicies,
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      policy
        ? {
            name: policy.name,
            nameI18n: policy.nameI18n,
            rateLimit: policy.rateLimit ?? '',
            burst: policy.burst ?? '',
            quotaGb: policy.quotaBytes === null ? null : policy.quotaBytes / BYTES_IN_GB,
            dailyQuotaGb:
              policy.dailyQuotaBytes === null ? null : policy.dailyQuotaBytes / BYTES_IN_GB,
            sessionTimeout: policy.sessionTimeout,
            idleTimeout: policy.idleTimeout,
            simultaneousUse: policy.simultaneousUse,
            interimInterval: policy.interimInterval,
            // ⚠️ Usiz forma FUP siyosatini "bo'sh" deb ochar va saqlash
            // uni TOZALAB yuborardi: kunlik kvota tugagach abonent
            // sekinlashish o'rniga to'liq tezlikda qolardi.
            fupPolicyId: policy.fupPolicyId,
            state: policy.state,
            timeWindows: policy.timeWindows,
          }
        : { state: 'ACTIVE', timeWindows: [] },
    )
  }, [open, policy, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // ⚠️ Tana KOMPONENTDA yig'ilmaydi — sabab `policyDraftBody` da:
      // `attributes` ko'rinmasdan qaytariladi va uni faqat CHAQIRIB
      // o'lchash mumkin.
      const draft = policyDraftBody(values, policy)
      return policy ? updatePolicy(policy.id, draft) : createPolicy(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.policies })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={policy ? t('catalog.policy.editTitle') : t('catalog.policy.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="name"
          label={t('catalog.policy.name')}
          rules={[{ required: true, message: t('tariffs.form.validation.nameRequired') }]}
        >
          <Input autoFocus />
        </Form.Item>

        <NameI18nFields />

        <Form.Item
          name="rateLimit"
          label={t('tariffs.rateLimit')}
          extra={t('tariffs.form.hintRate')}
          rules={[{ pattern: RATE_PATTERN, message: t('tariffs.form.validation.rateFormat') }]}
        >
          {/* ⚠️ `AutoComplete`, `Select` EMAS — sabab `RATE_PRESETS` izohida */}
          <AutoComplete
            options={RATE_PRESETS.map((preset) => ({ value: preset }))}
            filterOption={(input, option) =>
              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
            placeholder="50M/50M"
          />
        </Form.Item>

        <Form.Item
          name="burst"
          label={t('catalog.policy.burst')}
          extra={t('catalog.policy.burstHint')}
          rules={[{ pattern: BURST_PATTERN, message: t('catalog.policy.burstFormat') }]}
        >
          <Input placeholder="100M/100M 50M/50M 25M/25M 10" />
        </Form.Item>

        {/*
          ⚠️ SIYOSAT KVOTASI — PAKET KVOTASI EMAS, VA BU CHALKASHTIRILADI.

          Ikkalasi ham "GB" deb ataladi, lekin ular butunlay boshqa ish
          qiladi:
            · PAKET kvotasi — abonent SOTIB OLGAN hajm. Grant shundan
              yaratiladi, sarflangani ayiriladi, tugasa abonent uziladi.
              Bu — billing haqiqati.
            · SIYOSAT kvotasi — NAS'ga yuboriladigan texnik ship
              (`Mikrotik-Total-Limit`).

          ⚠️ VA U DEYARLI HAR DOIM USTIDAN YOZILADI: grantda qoldiq bor
          bo'lsa, `DefaultRadiusAuthorizeService.buildReply` siyosat
          qiymatini o'chirib, grant qoldig'ini qo'yadi ("shu abonentda
          aynan shuncha qoldi" — aniqroq ma'lumot). Ya'ni bu maydon FAQAT
          hajm chegarasi yo'q grantlarda (cheksiz paket) ishlaydi.

          Shuning uchun izohda aynan shu yozilgan: operator bu yerga
          "5 GB" yozib, paketdagi 100 GB o'rniga shu ishlaydi deb
          o'ylamasin.
        */}
        <Form.Item
          name="quotaGb"
          label={t('catalog.policy.quotaGb')}
          extra={<ExpandableHint text={t('catalog.policy.quotaHint')} fontSize={14} />}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder={t('billing.unlimited')} />
        </Form.Item>

        <Form.Item
          name="dailyQuotaGb"
          label={t('catalog.policy.dailyQuotaGb')}
          extra={t('catalog.policy.dailyQuotaHint')}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder={t('billing.unlimited')} />
        </Form.Item>

        {/*
          ⚠️ FUP — KUNLIK KVOTANING IKKINCHI YARMI, shuning uchun u AYNAN
          shu maydondan keyin turadi.

          Kunlik hajm tugagach `PolicyEnforcement` abonentni shu siyosat
          atributlariga o'tkazadi ("tugadi -> sekinlashdi"). Bo'sh
          qoldirilsa o'tish YO'Q. Ya'ni kunlik kvota tahrirlanadigan,
          uning oqibati esa ko'rinmaydigan holat forma o'zini o'zi
          tushuntirmaydigan qilardi — va har saqlashda o'sha oqibat
          jimgina o'chardi.

          ⚠️ Zanjir BIR QADAM: tanlangan siyosatning o'z FUP'i
          kuzatilmaydi (`PolicyEnforcement` javadoc'i). Ro'yxatdan
          siyosatning O'ZI chiqarib tashlanadi — bazada
          `policy_fup_not_self` CHECK turibdi va uni 400 ga qoldirish
          operatorga sababni ko'rsatmasdi.
        */}
        <Form.Item
          name="fupPolicyId"
          label={t('catalog.policy.fupPolicy')}
          extra={<ExpandableHint text={t('catalog.policy.fupPolicyHint')} fontSize={14} />}
        >
          <Select
            allowClear
            loading={policies.isPending}
            placeholder={t('catalog.policy.fupNone')}
            options={(policies.data ?? [])
              .filter((option) => option.id !== policy?.id)
              .map((option) => ({
                value: option.id,
                label: policyOptionLabel(option, i18n.language),
              }))}
          />
        </Form.Item>

        {/*
          ⚠️ `Session-Timeout` va `Idle-Timeout` faqat HOTSPOT uchun
          proyeksiyaga tushadi (`PolicyAttributes.of`): PPPoE va STATIC da
          ular ataylab olib tashlanadi. Shuning uchun izohda aytiladi —
          aks holda operator PPPoE siyosatiga qiymat yozib, u ishlamaganini
          ko'rardi.
        */}
        <Form.Item
          noStyle
          shouldUpdate={(prev: FormValues, next: FormValues) =>
            prev.sessionTimeout !== next.sessionTimeout
          }
        >
          {({ getFieldValue }) => {
            const value = getFieldValue('sessionTimeout') as number | null | undefined
            const suspicious =
              typeof value === 'number' && value > 0 && value < SUSPICIOUS_TIMEOUT_SECONDS
            return (
              <Form.Item
                name="sessionTimeout"
                label={t('tariffs.form.fieldSessionTimeout')}
                extra={t('catalog.policy.hotspotOnlyHint')}
                // ⚠️ `warning` — bloklamaydi, lekin jimgina o'tkazmaydi.
                // Sabab `SUSPICIOUS_TIMEOUT_SECONDS` izohida.
                validateStatus={suspicious ? 'warning' : undefined}
                help={
                  suspicious
                    ? t('catalog.policy.sessionTimeoutTooShort', { seconds: value })
                    : undefined
                }
              >
                <InputNumber min={1} style={{ width: '100%' }} addonAfter={t('units.duration.s')} />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item
          name="idleTimeout"
          label={t('tariffs.form.fieldIdleTimeout')}
          extra={t('catalog.policy.hotspotOnlyHint')}
        >
          <InputNumber min={1} style={{ width: '100%' }} addonAfter={t('units.duration.s')} />
        </Form.Item>

        <Form.Item
          name="simultaneousUse"
          // ⚠️ Yorliq QISQA, tushuntirish `extra` da. Ilgari yorliq o'rniga
          // butun bir jumla turardi (`terms.simultaneousUse`) va forma
          // ustunlari turli balandlikda "sakrardi".
          label={t('terms.simultaneousUseLabel')}
          extra={`${t('terms.simultaneousUseHint')} ${t('catalog.policy.simultaneousHint')}`}
        >
          <InputNumber min={1} style={{ width: '100%' }} placeholder={t('billing.unlimited')} />
        </Form.Item>

        {/*
          ⚠️ `Acct-Interim-Interval` — NAS'ga "har N soniyada sarflangan
          trafikni yubor" deydi. Bo'sh qoldirilsa servis sozlamasidagi
          qiymat ishlatiladi (60 s).

          ⚠️ Quyi chegara 15 s ATAYLAB: kichikroq qiymat "tezroq bo'lsin"
          degan niyat bilan qo'yiladi, lekin 10 000 sessiyada u ~667
          so'rov/sekund beradi va bazani bo'g'adi. Backend ham
          `@Min(15)` bilan rad etadi — bu yerda faqat qulaylik uchun,
          himoya u yerda.
        */}
        <Form.Item
          name="interimInterval"
          label={t('catalog.policy.interimInterval')}
          extra={t('catalog.policy.interimHint')}
        >
          <InputNumber
            min={15}
            style={{ width: '100%' }}
            addonAfter={t('units.duration.s')}
            placeholder={t('catalog.policy.interimDefault')}
          />
        </Form.Item>

        <TimeWindowFields />

        <Form.Item name="state" label={t('subscribers.columns.status')} rules={[{ required: true }]}>
          <Select options={POLICY_STATES.map((s) => ({ value: s, label: t(`policyState.${s}`) }))} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
